import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { BarChart3 } from 'lucide-react';
import { DailyCushionData } from '../types/cushion';
import { formatNumberDisplay } from '@/lib/utils';

interface CushionAverageTableProps {
  dailyData: DailyCushionData[];
  isLoading: boolean;
  error: string | null;
  processes: string[];
  title: string;
}

const CushionAverageTable: React.FC<CushionAverageTableProps> = ({ 
  dailyData, 
  isLoading, 
  error, 
  processes,
  title 
}) => {
  // Process configuration with Vietnamese names and colors
  const processConfig = {
    catVai: { name: 'Cắt vải', unit: 'M³', color: 'bg-blue-100' },
    may: { name: 'May', unit: 'M³', color: 'bg-green-100' },
    thanhPham: { name: 'Thành phẩm', unit: 'M³', color: 'bg-purple-100' },
    doMut: { name: 'Đổ mút', unit: 'M³', color: 'bg-teal-100' },
    catMut: { name: 'Cắt mút', unit: 'M³', color: 'bg-yellow-100' },
    gon: { name: 'Gòn', unit: 'Kg', color: 'bg-gray-100' },
  };

  // Calculate average M³/Kg for each process in the selected date range
  const averageStats = useMemo(() => {
    if (!dailyData.length) return {};

    const stats: Record<string, number> = {};

    processes.forEach(processKey => {
      // Get all values for this process (excluding zero days)
      let values: number[] = [];
      
      if (processKey === 'gon') {
        // For Gòn process, use weight (Kg)
        values = dailyData
          .map(day => day.processes.gon?.weight || 0)
          .filter(value => value > 0);
      } else {
        // For other processes, use volume (M³)
        values = dailyData
          .map(day => day.processes[processKey as keyof typeof day.processes]?.volume || 0)
          .filter(value => value > 0);
      }

      // Calculate average
      const average = values.length > 0 
        ? values.reduce((sum, value) => sum + value, 0) / values.length 
        : 0;

      stats[processKey] = average;
    });

    return stats;
  }, [dailyData, processes]);

  // Format numbers
  // Use Vietnamese locale formatting for numbers
  const getDecimals = (processKey: string) => {
    // For 'gon' process, use 1 decimal (Kg), otherwise 2 decimals (M³)
    return processKey === 'gon' ? 1 : 2;
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600">Lỗi tải dữ liệu: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : Object.keys(averageStats).length > 0 ? (
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center border-r font-semibold text-xs sm:text-sm px-2 sm:px-4 py-2 min-w-[120px] sm:min-w-[200px]">Chỉ tiêu</TableHead>
                  <TableHead className="text-center font-semibold text-xs sm:text-sm px-2 sm:px-4 py-2 min-w-[100px] sm:min-w-[150px]">Trung bình</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {processes.map((processKey, index) => {
                  const config = processConfig[processKey as keyof typeof processConfig];
                  const average = averageStats[processKey] || 0;
                  
                  return (
                    <TableRow key={processKey} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <TableCell className={`font-medium text-center border-r text-xs sm:text-sm px-2 sm:px-4 py-2 ${config?.color || 'bg-gray-100'}`}>
                        {config?.name || processKey}
                      </TableCell>
                      <TableCell className="text-center font-semibold text-xs sm:text-sm px-2 sm:px-4 py-2">
                        {formatNumberDisplay(average, getDecimals(processKey))} {config?.unit || 'M³'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Không có dữ liệu trung bình</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CushionAverageTable;